import { Cruise } from '../context/CruiseContext';
import { PublicCruiseView } from '../types/friends';
import { parseISO, startOfDay } from 'date-fns';

export type DateAvailability = 'user-only' | 'friend-only' | 'both' | 'available' | 'overlap';

/**
 * Checks if a date falls within a cruise's date range
 */
export const isDateInCruiseRange = (
  date: Date,
  cruise: Cruise | PublicCruiseView
): boolean => {
  try {
    const departureDate = parseISO(cruise.departureDate);
    const returnDate = parseISO(cruise.returnDate);
    const checkDate = startOfDay(date);
    const departure = startOfDay(departureDate);
    const returnD = startOfDay(returnDate);

    return (
      (checkDate.getTime() >= departure.getTime() &&
        checkDate.getTime() <= returnD.getTime()) ||
      checkDate.getTime() === departure.getTime() ||
      checkDate.getTime() === returnD.getTime()
    );
  } catch (error) {
    console.error('Error checking date in cruise range:', error);
    return false;
  }
};

/**
 * Gets all cruises that include a given date
 */
export const getCruisesForDate = (
  date: Date,
  cruises: Cruise[] | PublicCruiseView[]
): Array<Cruise | PublicCruiseView> => {
  return cruises.filter((cruise) => isDateInCruiseRange(date, cruise));
};

/**
 * Determines availability status for a given date
 */
export const getDateAvailability = (
  date: Date,
  userCruises: Cruise[],
  friendCruises: PublicCruiseView[],
  showUserCalendar: boolean,
  showFriendCalendar: boolean
): DateAvailability => {
  const userHasCruise = showUserCalendar
    ? userCruises.some((cruise) => isDateInCruiseRange(date, cruise))
    : false;
  const friendHasCruise = showFriendCalendar
    ? friendCruises.some((cruise) => isDateInCruiseRange(date, cruise))
    : false;

  if (userHasCruise && friendHasCruise) {
    return 'both';
  } else if (userHasCruise) {
    return 'user-only';
  } else if (friendHasCruise) {
    return 'friend-only';
  } else {
    // Neither user has a cruise on this date
    return 'available';
  }
};

/**
 * Finds date ranges where both users are available (no cruises for either)
 */
export interface DateRange {
  start: Date;
  end: Date;
}

export const getAvailableDateRanges = (
  userCruises: Cruise[],
  friendCruises: PublicCruiseView[],
  startDate: Date,
  endDate: Date
): DateRange[] => {
  const ranges: DateRange[] = [];
  let currentStart: Date | null = null;

  // Create a set of all booked dates
  const bookedDates = new Set<string>();
  
  [...userCruises, ...friendCruises].forEach((cruise) => {
    try {
      const departure = parseISO(cruise.departureDate);
      const returnD = parseISO(cruise.returnDate);
      const dep = startOfDay(departure);
      const ret = startOfDay(returnD);
      
      // Add all dates in the range
      let current = new Date(dep);
      while (current <= ret) {
        bookedDates.add(current.toISOString().split('T')[0]);
        current = new Date(current.getTime() + 24 * 60 * 60 * 1000);
      }
    } catch (error) {
      console.error('Error processing cruise dates:', error);
    }
  });

  // Find available ranges
  let current = new Date(startDate);
  const end = new Date(endDate);

  while (current <= end) {
    const dateStr = current.toISOString().split('T')[0];
    const isBooked = bookedDates.has(dateStr);

    if (!isBooked) {
      if (!currentStart) {
        currentStart = new Date(current);
      }
    } else {
      if (currentStart) {
        ranges.push({
          start: currentStart,
          end: new Date(current.getTime() - 24 * 60 * 60 * 1000),
        });
        currentStart = null;
      }
    }

    current = new Date(current.getTime() + 24 * 60 * 60 * 1000);
  }

  // Add final range if we ended on an available date
  if (currentStart) {
    ranges.push({
      start: currentStart,
      end: end,
    });
  }

  return ranges;
};

